import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Twitter, Youtube } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSiteContent } from "@/i18n/SiteContentContext";
import { company, services, countries, contact, socials as defaultSocials, siteSettings } from "@/data/content";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

export const Footer = () => {
  const { t } = useLang();
  const { get } = useSiteContent();
  const socials = get("socials", defaultSocials);
  const settings = get("settings", siteSettings);
  const socialLinks = [
    { key: "linkedin", Icon: Linkedin, label: "LinkedIn" },
    { key: "instagram", Icon: Instagram, label: "Instagram" },
    { key: "facebook", Icon: Facebook, label: "Facebook" },
    { key: "tiktok", Icon: TikTokIcon, label: "TikTok" },
    { key: "twitter", Icon: Twitter, label: "X (Twitter)" },
    { key: "youtube", Icon: Youtube, label: "YouTube" },
  ].filter((s) => socials[s.key]);
  return (
  <footer className="honeycomb-bg-light relative overflow-hidden bg-navy text-white">
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-6 flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-full bg-white p-1 shadow-lg ring-2 ring-gold/60">
              <img src="/assets/logo.png" alt="MMGT Logo" className="h-full w-full object-contain" />
            </span>
            <span className="font-jakarta text-base font-extrabold tracking-wide">MANDAIR MOVERS</span>
          </div>
          <p className="text-sm leading-relaxed text-white/50">
            {company.tagline} Premium general transport across the GCC. Est. {company.established} · License {company.license} · Reg. {company.registration}
          </p>
          <p className="mt-3 text-sm text-white/50">
            {contact.person} · {contact.phone} · {contact.email}
          </p>
          <div className="mt-6 flex gap-3">
            {socialLinks.map(({ key, Icon, label }) => (
              <a key={key} href={socials[key]} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-social-${key}`} className="grid h-10 w-10 cursor-pointer place-items-center rounded-full border border-white/15 text-white/60 transition-all duration-300 hover:border-gold hover:bg-gold hover:text-navy">
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="mb-6 font-jakarta text-sm font-bold uppercase tracking-[0.25em] text-gold">{t("footer.links")}</h4>
          <ul className="space-y-3 text-sm text-white/60">
            <li><a href="/#about" data-testid="footer-link-about" className="transition-colors hover:text-gold">{t("nav.about")}</a></li>
            <li><a href="/#fleet" data-testid="footer-link-fleet" className="transition-colors hover:text-gold">{t("nav.fleet")}</a></li>
            <li><Link to="/gallery" data-testid="footer-link-gallery" className="transition-colors hover:text-gold">{t("nav.gallery")}</Link></li>
            <li><Link to="/testimonials" data-testid="footer-link-testimonials" className="transition-colors hover:text-gold">Testimonials</Link></li>
            <li><Link to="/careers" data-testid="footer-link-careers" className="transition-colors hover:text-gold">{t("nav.careers")}</Link></li>
            <li><a href={settings.profile_pdf} download data-testid="footer-link-profile" className="transition-colors hover:text-gold">Company Profile (PDF)</a></li>
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="transition-colors hover:text-gold">Privacy Policy</Link></li>
            <li><Link to="/admin" data-testid="footer-link-admin" className="transition-colors hover:text-gold">Admin Login</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="mb-6 font-jakarta text-sm font-bold uppercase tracking-[0.25em] text-gold">{t("footer.services")}</h4>
          <ul className="space-y-3 text-sm text-white/60">
            {services.slice(0, 6).map((s) => (
              <li key={s.title}><a href="/#services" className="transition-colors hover:text-gold">{s.title}</a></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-6 font-jakarta text-sm font-bold uppercase tracking-[0.25em] text-gold">{t("footer.serve")}</h4>
          <ul className="space-y-3 text-sm text-white/60">
            {countries.map((c) => (
              <li key={c.name}>{c.name}</li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-white/40">{contact.office}</p>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row">
        <p>© {new Date().getFullYear()} {company.name}. {t("footer.rights")}</p>
        <p className="tracking-[0.2em] uppercase">{t("footer.tag")}</p>
      </div>
    </div>
  </footer>
  );
};
